import { useState } from 'react';
import { logger } from '../../lib/terminalLogger';
import { corelCommunication } from '../../lib/corel/communication';
import { useNotifications } from '../feedback/NotificationProvider';
import { notifyToolsError } from '../../lib/toolsErrorNotification';

/**
 * Unidades de medida suportadas pelo CorelDRAW (cdrUnit enum)
 */
const UNIT_OPTIONS = [
  { value: 3, label: 'mm' },
  { value: 4, label: 'cm' },
  { value: 7, label: 'm' },
  { value: 1, label: 'Polegada' },
  { value: 5, label: 'px' },
  { value: 14, label: 'pt' },
];

/**
 * Pontos de referência para redimensionamento (cdrReferencePoint enum)
 */
const REFERENCE_POINTS = [
  { value: 5, label: '↖', title: 'Topo Esquerda' },
  { value: 6, label: '↑', title: 'Topo Centro' },
  { value: 7, label: '↗', title: 'Topo Direita' },
  { value: 4, label: '←', title: 'Meio Esquerda' },
  { value: 8, label: '•', title: 'Centro' },
  { value: 9, label: '→', title: 'Meio Direita' },
  { value: 1, label: '↙', title: 'Base Esquerda' },
  { value: 2, label: '↓', title: 'Base Centro' },
  { value: 3, label: '↘', title: 'Base Direita' },
];

type ResizeMode = 'both' | 'width' | 'height';

export function OneResizePage() {
  const { notify } = useNotifications();
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [mode, setMode] = useState<ResizeMode>('width');
  const [unit, setUnit] = useState(4); // cm
  const [referencePoint, setReferencePoint] = useState(8); // center
  const [isSending, setIsSending] = useState(false);

  const unitLabel = UNIT_OPTIONS.find(u => u.value === unit)?.label ?? 'cm';

  const canApply = (() => {
    if (isSending) return false;
    if (mode === 'both') return width.trim() !== '' && height.trim() !== '';
    if (mode === 'width') return width.trim() !== '';
    if (mode === 'height') return height.trim() !== '';
    return false;
  })();

  const handleApply = async () => {
    if (!canApply) return;
    setIsSending(true);

    try {
      const data: Record<string, any> = {
        mode,
        unit,
        referencePoint,
      };

      if (mode === 'both' || mode === 'width') {
        data.width = parseFloat(width.replace(',', '.'));
      }
      if (mode === 'both' || mode === 'height') {
        data.height = parseFloat(height.replace(',', '.'));
      }

      if ((data.width !== undefined && (isNaN(data.width) || data.width <= 0)) ||
          (data.height !== undefined && (isNaN(data.height) || data.height <= 0))) {
        notify({ type: 'error', title: 'Valor inválido', message: 'Largura e altura devem ser números positivos.', sticky: false });
        return;
      }

      logger.debug('[OneResize] Enviando:', data);

      const result = await new Promise<any>((resolve) => {
        corelCommunication.sendActionGuarded('resize', data, (resp: any) => {
          resolve(resp);
        });
      });

      logger.debug('[OneResize] Resultado:', result);

      if (result?.status === 'ok') {
        const resized = result?.result?.objectsResized ?? 0;
        notify({
          type: 'success',
          title: 'Resize aplicado',
          message: `${resized} objeto(s) redimensionado(s).`,
          sticky: false,
        });
      } else {
        notifyToolsError(notify, 'OneResize', result?.message || 'Erro ao redimensionar.');
      }
    } catch (error: any) {
      logger.error('[OneResize] Erro:', error);
      notifyToolsError(notify, 'OneResize', error?.message || 'Erro inesperado.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canApply) {
      handleApply();
    }
  };

  // Quando muda o modo, ajusta campos habilitados
  const handleModeChange = (newMode: ResizeMode) => {
    setMode(newMode);
  };

  return (
    <div className="p-4 space-y-4">
      {/* Modo de redimensionamento */}
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">Modo</label>
        <div className="flex gap-1">
          {([
            { value: 'width' as ResizeMode, label: 'Pela Largura' },
            { value: 'height' as ResizeMode, label: 'Pela Altura' },
            { value: 'both' as ResizeMode, label: 'Largura + Altura' },
          ]).map((opt) => (
            <button
              key={opt.value}
              onClick={() => handleModeChange(opt.value)}
              className={`flex-1 px-2 py-1.5 text-xs rounded transition-colors ${
                mode === opt.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-navy-800 text-gray-400 hover:bg-navy-700 hover:text-gray-300'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-gray-500 mt-1">
          {mode === 'both' && 'Define largura e altura exatas (não-proporcional)'}
          {mode === 'width' && 'Define largura, altura calculada proporcionalmente'}
          {mode === 'height' && 'Define altura, largura calculada proporcionalmente'}
        </p>
      </div>

      {/* Largura e Altura */}
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-gray-400 mb-1">Largura ({unitLabel})</label>
          <input
            type="text"
            inputMode="decimal"
            value={width}
            onChange={(e) => setWidth(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={mode === 'height'}
            placeholder="0"
            className={`w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none ${
              mode === 'height' ? 'opacity-40 cursor-not-allowed' : ''
            }`}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Altura ({unitLabel})</label>
          <input
            type="text"
            inputMode="decimal"
            value={height}
            onChange={(e) => setHeight(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={mode === 'width'}
            placeholder="0"
            className={`w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none ${
              mode === 'width' ? 'opacity-40 cursor-not-allowed' : ''
            }`}
          />
        </div>
      </div>

      {/* Unidade de medida */}
      <div>
        <label className="block text-xs text-gray-400 mb-1">Unidade</label>
        <select
          value={unit}
          onChange={(e) => setUnit(parseInt(e.target.value))}
          className="w-full bg-navy-900 border border-navy-600 rounded px-2 py-1.5 text-sm text-gray-200 focus:border-blue-500 focus:outline-none"
        >
          {UNIT_OPTIONS.map((u) => (
            <option key={u.value} value={u.value}>{u.label}</option>
          ))}
        </select>
      </div>

      {/* Ponto de referência */}
      <div>
        <label className="block text-xs text-gray-400 mb-1.5">Ponto de Origem</label>
        <div className="grid grid-cols-3 gap-1 w-fit mx-auto">
          {REFERENCE_POINTS.map((rp) => (
            <button
              key={rp.value}
              title={rp.title}
              onClick={() => setReferencePoint(rp.value)}
              className={`w-8 h-8 flex items-center justify-center text-sm rounded transition-colors ${
                referencePoint === rp.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-navy-800 text-gray-400 hover:bg-navy-700 hover:text-gray-300'
              }`}
            >
              {rp.label}
            </button>
          ))}
        </div>
      </div>

      {/* Botão Aplicar */}
      <button
        onClick={handleApply}
        disabled={!canApply}
        className={`w-full py-2 rounded text-sm font-medium transition-colors ${
          canApply
            ? 'bg-blue-600 hover:bg-blue-700 text-white'
            : 'bg-navy-700 text-gray-500 cursor-not-allowed'
        }`}
      >
        {isSending ? 'Aplicando...' : 'Aplicar'}
      </button>
    </div>
  );
}
